import { Button } from '@/components/core/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogHeaderDefault,
} from '@/components/core/Dialog';
import { Loader } from '@/components/core/Loader';
import { useTranslation } from '@/hooks/useTranslation';
import { logger } from '@/services/logger';
import { DataModelAttribute } from '@/types/DataModel';

import { useAdditionalCategories } from '../queries';
import LazyLoadingTreeView from './TreeView';

interface AdditionalCategoryTreeDialogInterface {
    attribute: DataModelAttribute;
    handleSelect: (value: { value: string; label: string }) => void;
    onClose: () => void;
    open: boolean;
    selectedValue?: string;
    title: string;
}

const AdditionalCategoryTreeDialog = ({
    attribute,
    handleSelect,
    onClose,
    open,
    selectedValue,
    title,
}: AdditionalCategoryTreeDialogInterface) => {
    const { t } = useTranslation();

    const additionalCategoryName = attribute.params?.additionalCategory;
    const additionalDataModelName = attribute.params?.additionalModule;
    const extension = attribute.params?.extension;

    const { data: additionalCategoryTree, isLoading } = useAdditionalCategories({
        variables: {
            parent: additionalCategoryName ?? '',
            dataModel: additionalDataModelName ?? '',
            extension: extension ?? '',
        },
        enabled: attribute.params?.additionalCategory !== '',
    });

    if (!additionalDataModelName) {
        logger.error(`AdditionalCategoryDialog: Attribute ${attribute.name} has no additional module defined`, attribute);
        return null;
    }

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent
                onPointerDownOutside={(event) => event.preventDefault()}
                onInteractOutside={(event) => event.preventDefault()}
            >
                <DialogHeader>
                    <DialogHeaderDefault>
                        {title}
                    </DialogHeaderDefault>
                </DialogHeader>
                <DialogBody className="tw-flex tw-items-center tw-justify-center tw-h-96 tw-overflow-auto">

                    { isLoading ?
                        <Loader />
                        :
                        <LazyLoadingTreeView
                            additionalDataModelName={additionalDataModelName}
                            attribute={attribute}
                            extension={extension}
                            initialData={additionalCategoryTree ?? []}
                            onSelect={handleSelect}
                            selectedCategoryCode={selectedValue}
                        />
                    }

                </DialogBody>

                <DialogFooter >
                    <DialogClose asChild={true}>
                        <Button
                            variant="secondary"
                            onClick={() => onClose()}
                        >
                            {t('CLOSE')}
                        </Button>
                    </DialogClose>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default AdditionalCategoryTreeDialog;
